/**
 * Keyboard Shortcuts Coordination Store
 *
 * Prevents conflicts between single key and combination shortcuts:
 * - Single key release → One action (e.g., 'C' → Navigate to Changes tab)
 * - Key combinations → Different action (e.g., 'C+B' → Copy branch name)
 *
 * For example:
 * - When C+B is used, releasing C should NOT trigger tab navigation
 * - This pattern can be extended to other key combinations
 */

import { create } from 'zustand';

type KeyboardShortcutsState = {
  /**
   * Tracks which key combinations have been used
   * When a key is true, its standalone shortcut should be suppressed
   */
  keyCombinationUsed: {
    c: boolean;
    g: boolean;
    i: boolean; // For installation selection
    r: boolean; // For repository selection
    // Can be extended with other keys as needed:
    // j: boolean,
    // etc.
  };

  /** Marks that a key combination was used */
  setKeyCombinationUsed: (
    key: keyof KeyboardShortcutsState['keyCombinationUsed'],
    used: boolean,
  ) => void;
};

/** Coordinates keyboard shortcuts to prevent conflicts between keys and combinations */
export const useKeyboardShortcutsStore = create<KeyboardShortcutsState>((set) => ({
  keyCombinationUsed: {
    c: false,
    g: false,
    i: false,
    r: false,
  },
  setKeyCombinationUsed: (key, used) =>
    set((state) => ({
      keyCombinationUsed: {
        ...state.keyCombinationUsed,
        [key]: used,
      },
    })),
}));

export type KeyCombination = keyof KeyboardShortcutsState['keyCombinationUsed'];
